import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  Plus, 
  MoreHorizontal, 
  Pencil, 
  Trash2, 
  Loader2,
  UserPlus,
  Shield,
  User,
  Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { id: 'admin', label: 'Admin', description: 'Full access to all features', icon: Shield },
  { id: 'team_member', label: 'Team Member', description: 'Can manage investments and deals', icon: User },
  { id: 'lp', label: 'LP (Investor)', description: 'View-only access to portfolio', icon: Eye },
];

const RoleBadge = ({ role }) => {
  const colors = {
    admin: 'bg-purple-100 text-purple-700',
    team_member: 'bg-blue-100 text-blue-700',
    lp: 'bg-slate-100 text-slate-700',
  };
  
  return (
    <Badge className={colors[role] || colors.lp}>
      {role.replace('_', ' ')}
    </Badge>
  );
};

const emptyUser = {
  email: '',
  name: '',
  role: 'team_member'
};

export default function TeamPage() {
  const { getAuthHeaders, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState(emptyUser);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`, {
        headers: getAuthHeaders()
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email || '',
        name: user.name || '',
        role: user.role || 'team_member'
      });
    } else {
      setEditingUser(null);
      setFormData(emptyUser);
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || (!editingUser && !formData.email)) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      if (editingUser) {
        await axios.put(
          `${API_URL}/api/users/${editingUser.id}`,
          { name: formData.name, role: formData.role },
          { headers: getAuthHeaders() }
        );
        toast.success('User updated');
      } else {
        await axios.post(
          `${API_URL}/api/users`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast.success('User invited');
      }
      
      setDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    if (user.id === currentUser?.id) {
      toast.error("You can't delete yourself");
      return;
    }
    
    if (!window.confirm(`Remove ${user.name} from the team?`)) return;
    
    try {
      await axios.delete(`${API_URL}/api/users/${user.id}`, {
        headers: getAuthHeaders()
      });
      toast.success('User removed');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove user');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="team-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#1E3A5F]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">Team Management</h1>
          <p className="text-slate-500 mt-1">{users.length} team members</p>
        </div>
        <Button 
          className="btn-primary" 
          onClick={() => handleOpenDialog()}
          data-testid="invite-user-btn"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Invite User
        </Button>
      </div>

      {/* Role Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {ROLES.map((role) => {
          const count = users.filter(u => u.role === role.id).length;
          return (
            <Card key={role.id} className="metric-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-slate-100 rounded-lg">
                    <role.icon className="h-5 w-5 text-[#1E3A5F]" />
                  </div>
                  <div>
                    <p className="font-heading font-semibold text-slate-900">{role.label}</p>
                    <p className="text-xs text-slate-500">{role.description}</p>
                  </div>
                  <div className="ml-auto">
                    <span className="text-2xl font-heading font-light text-slate-900">{count}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Users Table */}
      <Card data-testid="users-table">
        <CardHeader>
          <CardTitle className="font-heading text-lg">All Team Members</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs uppercase tracking-widest">Name</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Email</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Role</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Joined</TableHead>
                <TableHead className="text-xs uppercase tracking-widest">Last Login</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                    No team members yet
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#1E3A5F] flex items-center justify-center text-white text-xs font-medium">
                          {user.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                        </div>
                        <span className="font-medium text-slate-900">{user.name}</span>
                        {user.id === currentUser?.id && (
                          <Badge variant="outline" className="text-xs">You</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">{user.email}</TableCell>
                    <TableCell>
                      <RoleBadge role={user.role} />
                    </TableCell>
                    <TableCell className="text-slate-600">{formatDate(user.created_at)}</TableCell>
                    <TableCell className="text-slate-600">{formatDate(user.last_login)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`user-actions-${user.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleOpenDialog(user)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          {user.id !== currentUser?.id && (
                            <DropdownMenuItem 
                              onClick={() => handleDelete(user)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Remove
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingUser ? 'Edit Team Member' : 'Invite Team Member'}
            </DialogTitle>
            <DialogDescription>
              {editingUser 
                ? 'Update the team member details' 
                : 'Add a new team member. They will receive login access via email OTP.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {!editingUser && (
              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="john@berkshireafrica.com"
                  data-testid="user-email"
                />
              </div>
            )}
            
            <div>
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="John Doe"
                data-testid="user-name"
              />
            </div>

            <div>
              <Label htmlFor="role">Role</Label>
              <Select 
                value={formData.role} 
                onValueChange={(value) => setFormData({...formData, role: value})}
              >
                <SelectTrigger data-testid="user-role">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map(role => (
                    <SelectItem key={role.id} value={role.id}>
                      <div className="flex items-center gap-2">
                        <role.icon className="h-4 w-4" />
                        <span>{role.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                {ROLES.find(r => r.id === formData.role)?.description}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              className="btn-primary" 
              onClick={handleSave}
              disabled={saving}
              data-testid="save-user-btn"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editingUser ? 'Update' : 'Invite'} User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
